import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import { motion } from "framer-motion";

const CONTRACT_ADDRESS = "0x01D675A61d94aEa570007374d85301d6619D9E6e";
// DOGE_ADDRESS removed – native DOGE is used via provider.getBalance()
const MCRIB_ADDRESS = "0xbdaD927604c5cB78F15b3669a92Fa5A1427d33a2";

const ABI = [
  // PAYABLE mint (so we can send DOGE as msg.value)
  "function mint(uint256 num) external payable",
  // Dynamic pricing / reveal config
  "function dogeFee() external view returns (uint256)",
  "function mcribPrice() external view returns (uint256)",
  "function revealBlock() external view returns (uint256)",
  "function revealed() external view returns (bool)",
  // Core ERC721-ish stuff
  "function tokenURI(uint256 id) external view returns (string)",
  "function totalSupply() external view returns (uint256)",
  "function ownerOf(uint256 tokenId) external view returns (address)",
  "function balanceOf(address owner) external view returns (uint256)",
  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
  // NEW ERC721Enumerable FUNCTIONS — FOR RANDOM ID TRACKING
  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
  "function tokenOfOwnerByIndex(address owner, uint256 index) external view returns (uint256)",
  "function tokenByIndex(uint256 index) external view returns (uint256)",
  "function isEaten(uint256) external view returns (bool)",
  "function tokenBalance(uint256 id) external view returns (uint256)",
  "function eatMcRib(uint256 id) external"
];

const ERC20_ABI = [
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function balanceOf(address account) external view returns (uint256)"
];

const TX_OPTIONS = {
  gasPrice: ethers.utils.parseUnits("250", "gwei"),
  // gasLimit removed → now calculated dynamically inside handleMint
};
const DOGECHAIN = {
  chainId: "0x7D0",
  chainName: "Dogechain",
  rpcUrls: ["https://rpc.dogechain.dog"],
  nativeCurrency: { name: "DOGE", symbol: "DOGE", decimals: 18 },
  blockExplorerUrls: ["https://explorer.dogechain.dog"]
};

// MCRIB_REQUIRED removed – using contract.mcribPrice() dynamically
 // 50B × 10¹⁵ (not used now; left in for reference)
// DOGE_REQUIRED removed – using contract.dogeFee() dynamically
 // not used now; dynamic from contract

const TRAIT_RARITY = {
  buns: { "Standard": 40, "Toasted": 35, "Extra Crispy": 25 },
  patties: { "Classic": 30, "Smoky": 30, "Double Stack": 25, "Charred": 15 },
  sauces: { "BBQ Drip": 40, "Spicy Glaze": 35, "Sweet Heat": 25 },
  onions: { "None": 20, "Light": 25, "Heavy": 20, "Crispy": 20, "Caramelized": 15 },
  pickles: { "None": 30, "Single": 30, "Double": 25, "Triple": 15 },
  topBuns: { "Sesame": 60, "Plain": 40 },
  bottomBun: {
    "Standard Bottom": 67,
    "Toasted Edge": 20,
    "Buttered Glow": 10,
    "Charred Perfection": 3 // ← 3% CHANCE — LEGENDARY
  }
};

let provider, signer, contract;
if (typeof window !== "undefined" && window.ethereum) {
  provider = new ethers.providers.Web3Provider(window.ethereum);
  signer = provider.getSigner();
  contract = new ethers.Contract(CONTRACT_ADDRESS, ABI, signer);
}

export default function App() {
const NFTS_PER_PAGE = 25; // Constant for pagination limit
  const [account, setAccount] = useState("");
  const [recentMints, setRecentMints] = useState([]);
  const [myNFTs, setMyNFTs] = useState([]);
  const [mintedCount, setMintedCount] = useState(0);
  const [cooking, setCooking] = useState(false);
  const [mintStep, setMintStep] = useState("");
  const [selectedNFT, setSelectedNFT] = useState(null);
  const [activeTab, setActiveTab] = useState("recent");
  const [userMcribBalance, setUserMcribBalance] = useState("0");
  const [userDogeBalance, setUserDogeBalance] = useState("0"); // ← NATIVE DOGE (MetaMask's DOGE)
  const [isEating, setIsEating] = useState(false);
  const [mcribContract, setMcribContract] = useState(null);
  const [allTokenIDs, setAllTokenIDs] = useState([]); 
  const [tokenIDCache, setTokenIDCache] = useState({}); // NEW: Multi-wallet cache { 'addr': [ids] }
  const [isTokenListLoading, setIsTokenListLoading] = useState(false); // NEW: Background loading state // All IDs owned by user
  const [currentPage, setCurrentPage] = useState(1); // Current pagination page

  const BACKGROUND_RARITIES = {
    "Off-White Background": "89.5%",
    "Classic McDonalds Wrapper": "1.0%",
    "Red Arches Tray": "2.0%",
    "Gray/White Tray": "3.0%",
    "Night sky vibes": "4.0%",
    "Golden Tray": "0.5%",
  };

  useEffect(() => {
    if (provider && account) {
      const signerInstance = provider.getSigner();
      setMcribContract(new ethers.Contract(MCRIB_ADDRESS, ERC20_ABI, signerInstance));
    }
  }, [provider, account]);

  const handleEatMcRib = async (tokenId) => {
    if (!selectedNFT || !mcribContract || !contract) return;
  
    setIsEating(true);
    try {
      const signerInstance = provider.getSigner();
      const eatContract = new ethers.Contract(CONTRACT_ADDRESS, ABI, signerInstance);
  
      const requiredTokens = await eatContract.tokenBalance(tokenId);
      if (requiredTokens.isZero()) throw new Error("This McRib has no tokens remaining!");
  
      const allowance = await mcribContract.allowance(account, CONTRACT_ADDRESS);
  
      if (allowance.lt(requiredTokens)) {
        alert("Approving MCRIB tokens for the contract...");
        let txn = await mcribContract.approve(CONTRACT_ADDRESS, requiredTokens.mul(10));
        await txn.wait();
      }
  
      alert(`Executing eatMcRib for token #${tokenId}...`);
      let txn = await eatContract.eatMcRib(tokenId);
      await txn.wait();
  
      alert(`McRib #${tokenId} has been successfully eaten!`);

      // CRITICAL FIX: Refresh all states and the expanded card after transaction confirmation.
      // 1. Refresh balances and token list.
      loadBalances();
      await loadOwnedTokenIDs(); 

      // 2. Force a re-fetch of the single eaten NFT's metadata and update the expanded card.
      const eatenTokenId = selectedNFT.id;
      // fetchAndCacheMetadata will hit the network and update the cache for this single item.
      const freshNFTMetadata = await fetchAndCacheMetadata(eatenTokenId, contract, false); 
      if (freshNFTMetadata) {
          setSelectedNFT(freshNFTMetadata); // Updates the expanded card view (shows the "eaten" layer)
      }

      // 3. Reload the main page list (which uses the now-updated cache).
      await loadMyMcRibs(currentPage); 

      // REMOVED: setSelectedNFT(null); - The card will now stay open and refreshed until the user closes it!
    } catch (error) {
      console.error("Error eating McRib:", error);
      alert(`Failed to eat McRib: ${error.reason || error.message || "Transaction failed."}`);
    } finally {
      setIsEating(false);
    }
  };

  // ————————————————————————————————————
  // ALL FUNCTIONS — IN CORRECT ORDER TO AVOID "before initialization" ERRORS
  // ————————————————————————————————————
  const switchToDogechain = async () => {
    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: DOGECHAIN.chainId }]
      });
    } catch (e) {
      if (e.code === 4902) {
        await window.ethereum.request({
          method: "wallet_addEthereumChain",
          params: [DOGECHAIN]
        });
      }
    }
  };

const connectWallet = async () => {
  try {
    await switchToDogechain();
    const accounts = await provider.send("eth_requestAccounts", []);
    setAccount(accounts[0]);

    // ←←← THIS LINE FIXES AUTO-REFRESH ON RECONNECT
    await loadMyMcRibs(currentPage);  // ← keep whatever page the user was on
    await loadBalances();          // ← also updates DOGE/MCRIB balances
    if (activeTab === "mynfts") {  // ← if you're already on My McRibs tab
      setMyNFTs([]);               // quick flicker so you see it refresh
      await new Promise(r => setTimeout(r, 100));
      await loadMyMcRibs(currentPage);  // ← keep whatever page the user was on
    }
    // ←←← END OF FIX
  } catch (e) {
    alert("Wallet connection failed: " + e.message);
  }
};

  const disconnectWallet = () => {
    setAccount("");
    setMyNFTs([]);
    setSelectedNFT(null);
    setUserMcribBalance("0");
    setUserDogeBalance("0");
    setAllTokenIDs([]); // CRITICAL FIX: Clear the list of owned token IDs on disconnect
  };

  // Pulls NATIVE DOGE balance (MetaMask's "DOGE")
  const loadBalances = async () => {
    if (!account) return;
    try {
      const mcrib = new ethers.Contract(MCRIB_ADDRESS, ERC20_ABI, provider);
      const nativeDogeBal = await provider.getBalance(account); // ← NATIVE DOGE
      const mcribBalRaw = await mcrib.balanceOf(account);
      setUserMcribBalance(ethers.utils.formatUnits(mcribBalRaw, 15));
      setUserDogeBalance(ethers.utils.formatEther(nativeDogeBal)); // ← THIS IS THE ONE YOU WANT
    } catch (e) {
      console.error("Balance load failed", e);
    }
  };

const loadData = async () => {
  try {
    const supply = await contract.totalSupply();
    const count = supply.toNumber();
    setMintedCount(count);

    const recent = [];
    const cutoffTime = Date.now() - 24 * 60 * 60 * 1000; // exactly 24 hours ago

    // Scan last 100 minted (plenty) to find ones from last 24h
    for (let i = 0; i < Math.min(count, 100); i++) {
      if (recent.length >= 20) break; // stop early if we have enough

      try {
        const tokenId = await contract.tokenByIndex(count - 1 - i);
        const uri = await contract.tokenURI(tokenId);
        const metadata = await parseTokenURI(uri, tokenId.toString());

        // Estimate mint time: (10000 - tokenId) × ~2 seconds per block
        const approxMintTimestamp = (10000 - parseInt(tokenId)) * 2000;

        if (Date.now() - approxMintTimestamp <= cutoffTime) {
          recent.push({ id: tokenId.toString(), metadata });
        }
      } catch (err) {
        console.warn("Failed token", count - 1 - i, err);
      }
    }

    setRecentMints(recent);
  } catch (e) {
    console.error("loadData failed:", e);
  }
};

// ====================================================================
// HELPER FUNCTION: Handles the fetch from the contract and updates the cache.
// ====================================================================
const fetchAndCacheMetadata = async (id, contract, isBackgroundUpdate) => {
    try {
        const uri = await contract.tokenURI(id);
        
        // Use the existing parseTokenURI logic which handles raw JSON or Base64
        const metadata = parseTokenURI(uri, id.toString());
        
        // Simple data structure for the NFT in the UI
        const nftData = {
            id: id,
            metadata: metadata,
        };
        
        // Cache the data in localStorage
        localStorage.setItem(`mcrib_nft_${id}`, JSON.stringify(nftData));
        
        return nftData; 

    } catch (e) {
        console.error(`Failed to fetch/cache metadata for #${id}:`, e);
        return null;
    }
};

// ====================================================================
// NEW DEDICATED FUNCTION: Loads the raw list of owned token IDs from the contract.
// This is the function we will call after an 'eat' event.
// ====================================================================
const loadOwnedTokenIDs = async () => {
  if (!account || !contract) return [];
  try {
    const balance = await contract.balanceOf(account);
    const tokenIds = [];
    for (let i = 0; i < balance.toNumber(); i++) {
      const id = await contract.tokenOfOwnerByIndex(account, i);
      tokenIds.push(id.toNumber());
    }
    setAllTokenIDs(tokenIds);
    return tokenIds;
  } catch (e) {
    console.error("Failed to load owned token IDs:", e);
    return [];
  }
};

// ====================================================================
// FINAL LOAD MY MCRIBS — WITH LOADING TEXT + RANGE DISPLAY
// ====================================================================
const loadMyMcRibs = async (page = 1) => {
  if (!account || !contract) return;

  // ←←← NEW: show loading with exact page
  const totalOwned = allTokenIDs.length || 0;
  const startNum = (page - 1) * NFTS_PER_PAGE + 1;
  const endNum = Math.min(page * NFTS_PER_PAGE, totalOwned);
  setMintStep(`Loading page ${page}… (${startNum}–${endNum})`);

let tokenIds = allTokenIDs; // use cached IDs if we have them
  if (tokenIds.length === 0) {
    tokenIds = await loadOwnedTokenIDs(); // Now calls the new, dedicated function
    if (tokenIds.length === 0) {
      setMintStep("Failed to load your McRibs");
      return;
    }
  }

  const startIndex = (page - 1) * NFTS_PER_PAGE;
  const endIndex = startIndex + NFTS_PER_PAGE;
  const pageTokenIDsForPage = tokenIds.slice(startIndex, endIndex);

  // Show something instantly so it never goes blank
  setMyNFTs([]); // clear old page

// CRITICAL NON-LAZY FIX: Always fetch the newest data and update the cache.
  // The cache is now an accelerator, not the source of truth.
  const loaded = [];
  for (const id of pageTokenIDsForPage) {
    // By calling fetchAndCacheMetadata() every time, we hit the network, 
    // retrieve the LATEST tokenURI, and OVERWRITE/UPDATE localStorage.
    const fresh = await fetchAndCacheMetadata(id, contract, false);
    if (fresh) loaded.push(fresh);
  }

  setMyNFTs(loaded);
  setCurrentPage(page);
  setMintStep(null); // hide loading text
};

  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
  // UNIVERSAL tokenURI PARSER — WORKS EVERY TIME
  // ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←

// FORCE PRE-REVEAL SCREEN — bypasses all parsing issues
const forcePreRevealImage = (tokenId) => {
  return `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIGZpbGw9IiMwMDAwMDAiLz48dGV4dCB4PSIxNiIgeT0iOSIgZm9udC1mYW1pbHk9Im1vbm9zcGFjZSIgZm9udC1zaXplPSIyLjgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiNGRjZCMDAiPk5PVyBTRVJWSU5HPC90ZXh0Pjx0ZXh0IHg9IjE2IiB5PSIxNiIgZm9udC1mYW1pbHk9Im1vbm9zcGFjZSIgZm9udC1zaXplPSI1IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjRkZGRkZGIiBzdHJva2U9IiNGRjZCMDAiIHN0cm9rZS13aWR0aD0iMC44Ij5NY1JJQiAje3Rva2VuSWR9PC90ZXh0Pjx0ZXh0IHg9IjE2IiB5PSIyNCIgZm9udC1mYW1pbHk9Im1vbm9zcGFjZSIgZm9udC1zaXplPSIxLjgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM4ODg4ODgiPlJldmVhbHMgaW4gYmxvY2tzPC90ZXh0Pjwvc3ZnPg==`.replace("{{tokenId}}", tokenId);
};

// ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←
// FINAL UNIVERSAL tokenURI PARSER — WORKS WITH RAW JSON (current contract)
// ALSO STILL WORKS IF SOMEONE RETURNS BASE64 (future-proof)
// ←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←←

const parseTokenURI = (uri, tokenId) => {
  try {
    let jsonString;

    // CASE 1: Standard JSON string (your new contract)
    if (uri.startsWith("{")) {
      jsonString = uri;
    }

    // CASE 2: Base64 data URI (old style)
    else if (uri.startsWith("data:application/json")) {
      const b64 = uri.split("base64,")[1];
      jsonString = atob(b64);
    }

    // Parse cleanly (NO escape/unescape nonsense)
    const json = JSON.parse(jsonString);

    return {
      name: json.name || `McRib #${tokenId}`,
      description: json.description || "Fresh McRib. Ready to eat!",
      image: json.image,
      attributes: json.attributes || []
    };
  } catch (e) {
    console.error("Metadata parse failed for token", tokenId, e);
    console.log("RAW URI:", uri);

    // DO NOT RETURN 32×32 fallback
    return {
      name: `McRib #${tokenId}`,
      description: "Loading…",
      image: null,
      attributes: []
    };
  }
};

const [mintQuantity, setMintQuantity] = useState(1);

const handleMint = async () => {
  if (!account) return alert("Connect wallet first");
  if (mintQuantity < 1 || mintQuantity > 20) {
    return alert("You can only mint 1–20 McRibs at a time");
  }

  setCooking(true);
  setMintStep(`Preparing grill for ${mintQuantity} McRib${mintQuantity > 1 ? "s" : ""}...`);

  try {
    const mcrib = new ethers.Contract(MCRIB_ADDRESS, ERC20_ABI, signer);

    // ============================
    // DEBUG START — BEFORE MINT
    // ============================
    console.log("===== MULTI-MINT DEBUG START =====");
    console.log(`Minting quantity: ${mintQuantity}`);

    const dogeBefore = await provider.getBalance(account);
    const mcribBefore = await mcrib.balanceOf(account);
    console.log("DOGE BEFORE:", ethers.utils.formatEther(dogeBefore));
    console.log("MCRIB BEFORE:", ethers.utils.formatUnits(mcribBefore, 15));

    const dogeFeePer = await contract.dogeFee();
    const mcribPricePer = await contract.mcribPrice();

    const totalDogeRequired = dogeFeePer.mul(mintQuantity);
    const totalMcribRequired = mcribPricePer.mul(mintQuantity);

    console.log("Per mint — DOGE fee:", ethers.utils.formatEther(dogeFeePer));
    console.log("Per mint — MCRIB price:", ethers.utils.formatUnits(mcribPricePer, 15));
    console.log(`TOTAL REQUIRED → DOGE: ${ethers.utils.formatEther(totalDogeRequired)} | MCRIB: ${ethers.utils.formatUnits(totalMcribRequired, 15)}B`);

    // ============================
    // CHECK REMAINING SUPPLY
    // ============================
    setMintStep("Checking remaining supply...");
    const currentSupply = await contract.totalSupply();
    if (currentSupply.add(mintQuantity).gt(10000)) {
      throw new Error(`Only ${10000 - currentSupply.toNumber()} McRibs left — can't mint ${mintQuantity}`);
    }

    // ============================
    // 1. CHECK NATIVE DOGE BALANCE
    // ============================
    setMintStep("Checking DOGE balance...");
    const nativeBalance = await provider.getBalance(account);
    if (nativeBalance.lt(totalDogeRequired)) {
      throw new Error(
        `Not enough DOGE! Need ${ethers.utils.formatEther(totalDogeRequired)} DOGE (${mintQuantity} × ${ethers.utils.formatEther(dogeFeePer)})`
      );
    }

    // ============================
    // LEGACY GAS & SAFE LIMITS (The Fix: Simple & Separate)
    // ============================
    setMintStep("Setting Dynamic Gas Options...");
    
    // --- Dynamic Gas Limit Calculation ---
    // These constants are estimated based on your contract's logic (looping/random ID generation).
    const PER_MINT_COST = 400000; // Estimated gas cost for one mint loop iteration
    const BASE_OVERHEAD = 500000; // Baseline gas cost for function setup
    const MAX_LIMIT = 10000000;   // Hard cap at 10 Million to prevent sending excessive gas

    const calculatedGasLimit = BASE_OVERHEAD + (mintQuantity * PER_MINT_COST);
    
    // Use the calculated limit, or the MAX_LIMIT if the calculation exceeds it (for safety)
    const finalGasLimit = calculatedGasLimit > MAX_LIMIT ? MAX_LIMIT : calculatedGasLimit;

    // Inherit gasPrice from TX_OPTIONS, then add safe gasLimit for each transaction.
    const approveTxOptions = { ...TX_OPTIONS, gasLimit: ethers.BigNumber.from(400000) }; // 400k safe limit for approval
    const mintTxOptions = { 
      ...TX_OPTIONS, 
      gasLimit: ethers.BigNumber.from(finalGasLimit) // <-- NOW DYNAMICALLY SCALED!
    }; 
    // ============================
    // 2. APPROVE MCRIB (if needed)
    // ============================
    setMintStep("Checking MCRIB approval...");
    const allowance = await mcrib.allowance(account, CONTRACT_ADDRESS);
    
    // 🛑 CRITICAL DEBUG LINES 🛑
    console.log("DEBUG: Contract Address Being Checked:", CONTRACT_ADDRESS);
    console.log("DEBUG: Current Allowance:", allowance.toString());
    console.log("DEBUG: Required MCRIB:", totalMcribRequired.toString());
    // 🛑 🛑 🛑 🛑 🛑 🛑 🛑 🛑 🛑 🛑

    if (allowance.lt(totalMcribRequired)) {
      setMintStep(`Approving ${ethers.utils.formatUnits(totalMcribRequired, 15)}B MCRIB...`);
      const approveTx = await mcrib.approve(
        CONTRACT_ADDRESS,
        totalMcribRequired.mul(10), // generous buffer
        approveTxOptions // <-- USES CORRECT, SEPARATE APPROVE OPTIONS
      );
      await approveTx.wait();
      console.log("MCRIB APPROVED FOR", ethers.utils.formatUnits(totalMcribRequired.mul(10), 15) + "B");
    } else {
      console.log("MCRIB already approved");
    }

    // ============================
    // 3. MINT MULTIPLE McRIBS
    // ============================
    setMintStep(`Grilling ${mintQuantity} McRib${mintQuantity > 1 ? "s" : ""}...`);
    console.log("Sending mint tx with value:", ethers.utils.formatEther(totalDogeRequired), "DOGE");

    const mintTx = await contract.mint(mintQuantity, {
      ...mintTxOptions,    // ← USES CORRECT, SEPARATE MINT OPTIONS
      value: totalDogeRequired
    });

    setMintStep("On the blockchain... almost there!");
    const receipt = await mintTx.wait();
    console.log("MINT TX SUCCESS:", receipt.transactionHash);

    // ============================
    // DEBUG END — AFTER MINT
    // ============================
    const dogeAfter = await provider.getBalance(account);
    const mcribAfter = await mcrib.balanceOf(account);

    console.log("DOGE AFTER:", ethers.utils.formatEther(dogeAfter));
    console.log("MCRIB AFTER:", ethers.utils.formatUnits(mcribAfter, 15));
    console.log("DOGE DEDUCTED:", ethers.utils.formatEther(dogeBefore.sub(dogeAfter)));
    console.log("MCRIB DEDUCTED:", ethers.utils.formatUnits(mcribBefore.sub(mcribAfter), 15));
    console.log("===== MULTI-MINT DEBUG END =====");

// ============================
    // SUCCESS & REFRESH (FINAL NON-LAG FIX)
    // ============================
    setMintStep("McRibs served fresh! Reloading tray...");

    // CRITICAL FIX: The hard-coded 6-second delay has been REMOVED.
    // The existing loadData() is kept to update supply/pricing (which is slow but necessary).
    // We now insert the crucial loadOwnedTokenIDs() call to update the list of owned NFTs
    // immediately, which resolves the lag.
    await loadData(); // Updates total supply, reveal data, fees, etc.
    await loadBalances();
    // await new Promise(r => setTimeout(r, 6000)); <--- DELAY IS REMOVED
    await loadOwnedTokenIDs(); // CRITICAL: Forces the raw list of owned NFTs to update immediately.
    await loadMyMcRibs(1);     // Refreshes the display to show the new tokens on page 1.
    
    alert(`${mintQuantity} McRib${mintQuantity > 1 ? "s" : ""} successfully minted!`);

  } catch (error) {
    console.error("Mint failed:", error);

    // Keep your smart fallback error message
    try {
      const fee = await contract.dogeFee();
      const price = await contract.mcribPrice();
      const totalDoge = fee.mul(mintQuantity);
      const totalMcrib = price.mul(mintQuantity);

      alert(
        `Mint failed — Required for ${mintQuantity}:\n` +
        `${ethers.utils.formatEther(totalDoge)} DOGE + ${ethers.utils.formatUnits(totalMcrib, 15)}B MCRIB\n\n` +
        (error.message || "Check wallet & balances")
      );
    } catch {
      alert("Mint failed — check network, balances, or try again.");
    }
  } finally {
    setTimeout(() => {
      setCooking(false);
      setMintStep("");
    }, 3000);
  }
};

useEffect(() => {
    if (account) {
      loadBalances();
      loadMyMcRibs(currentPage); // <-- CRITICAL FIX: Pass currentPage to loadMyMcRibs
    }
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, currentPage]);

  return (
    // YOUR ENTIRE BEAUTIFUL JSX — 100% UNTOUCHED
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating McRib Background */}
      <div className="fixed inset-0 bg-floating-mcrib opacity-10" />
      {/* Fire & Smoke Title Effect */}
      <div className="fixed top-0 left-0 right-0 flex justify-center pointer-events-none z-20">
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ repeat: Infinity, duration: 4 }}
          className="fire-glow"
        >
          <h1 className="text-8xl font-pixel text-mcrib-fire opacity-60 blur-xl select-none">
            McRIB PIXELS
          </h1>
        </motion.div>
      </div>
      {/* Minting Overlay */}
      {cooking && (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col items-center justify-center gap-6">
          <motion.h1
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="text-6xl font-pixel text-mcrib-gold"
          >
            ...COOKING...
          </motion.h1>
          <div className="w-96 h-8 bg-gray-800 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-mcrib-red via-mcrib-fire to-mcrib-gold"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 3, ease: "linear" }}
            />
          </div>
          <p className="text-xl text-mcrib-gold font-pixel">{mintStep}</p>
        </div>
      )}
      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="text-center pt-20 relative z-10"
      >
        <h1 className="text-7xl font-pixel text-mcrib-gold drop-shadow-2xl relative">
          McRIB PIXELS
          <div className="smoke-particles" />
        </h1>
        <p className="text-2xl mt-4 text-mcrib-red font-bold">
          50B $MCRIB + 1 DOGE
        </p>
        {/* Balance Display — Shows Native DOGE (MetaMask's DOGE) */}
        {account && (
          <div className="mt-4 text-lg font-pixel">
            <span className="text-mcrib-gold">
              MCRIB: {parseFloat(userMcribBalance).toFixed(2)}B
            </span>{" "}
            |{" "}
            <span className="text-mcrib-gold">
              DOGE: {parseFloat(userDogeBalance).toFixed(3)}
            </span>
          </div>
        )}
        <div className="flex justify-center gap-6 mt-8">
          {account ? (
            <div className="flex gap-4 items-center">
              <span className="text-green-400 font-bold">
                {account.slice(0, 6)}...{account.slice(-4)}
              </span>
              <button
                onClick={disconnectWallet}
                className="bg-gray-700 px-4 py-2 rounded-full text-sm"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <button
              onClick={connectWallet}
              className="bg-mcrib-red px-8 py-4 rounded-full text-xl font-bold hover:scale-110 transition"
            >
              CONNECT WALLET
            </button>
          )}
        </div>

        {/* === NEW MULTI-MINT SECTION === */}
        <div className="flex flex-col items-center gap-8 mt-12">
          <div className="flex items-center gap-8">
            {[1, 5, 10, 20].map((num) => (
              <button
                key={num}
                onClick={() => setMintQuantity(num)}
                disabled={cooking}
                className={`px-8 py-4 rounded-full font-bold text-2xl transition-all shadow-lg ${
                  mintQuantity === num
                    ? "bg-mcrib-gold text-black scale-110 shadow-2xl"
                    : "bg-mcrib-red/80 text-white hover:scale-105"
                } disabled:opacity-50`}
              >
                {num === 20 ? "MAX" : num}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-6">
            <input
              type="number"
              min="1"
              max="20"
              value={mintQuantity}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 1;
                if (val >= 1 && val <= 20) setMintQuantity(val);
                else if (val > 20) setMintQuantity(20);
              }}
              className="w-32 px-6 py-4 bg-black/80 border-4 border-mcrib-gold rounded-2xl text-center text-4xl font-pixel text-mcrib-gold focus:outline-none focus:border-mcrib-fire"
              disabled={cooking}
            />
            <span className="text-3xl font-pixel text-mcrib-gold">McRibs</span>
          </div>

          {account && (
            <div className="text-center">
              <p className="text-2xl font-pixel text-gray-400">Total Cost:</p>
              <p className="text-4xl font-pixel text-mcrib-gold mt-2">
                {(mintQuantity * 1).toFixed(2)} DOGE + {(mintQuantity * 50).toFixed(0)}B $MCRIB
              </p>
              <p className="text-sm text-gray-500 mt-3 font-pixel">
                (Exact price pulled from contract)
              </p>
            </div>
          )}

          <motion.button
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.96 }}
            onClick={handleMint}
            disabled={cooking || !account}
            className="bg-gradient-to-r from-mcrib-fire to-mcrib-gold text-black px-20 py-8 rounded-full text-5xl font-bold shadow-3xl disabled:opacity-50 disabled:cursor-not-allowed mt-8"
          >
            {cooking ? "...COOKING..." : `MINT ${mintQuantity} McRIB${mintQuantity > 1 ? "S" : ""}!`}
          </motion.button>
        </div>
      </motion.header>
                  {/* Tabs — CLEAN, NO COMMENTS, NO BULLSHIT */}
      <div className="flex justify-center gap-12 mt-12 text-3xl font-pixel tracking-wider">
        <button
          onClick={() => setActiveTab("recent")}
          className={activeTab === "recent" ? "text-mcrib-gold drop-shadow-lg" : "text-gray-500"}
        >
          FRESH OFF THE GRILL
        </button>
        <button
  onClick={() => {
    setActiveTab("mynfts");
    loadMyMcRibs(currentPage);  // ← NOW PASSES THE CURRENT PAGE
  }}
  className={activeTab === "mynfts" ? "text-mcrib-gold drop-shadow-lg" : "text-gray-500"}
>
  MY McRIBS ({allTokenIDs.length})
</button>
      </div>

      {/* Traits Grid — NOW WITH BACKGROUNDS FIRST */}
      <div className="max-w-6xl mx-auto mt-16 px-4 grid grid-cols-2 md:grid-cols-3 gap-8">
        {[
          { trait: "Background", options: BACKGROUND_RARITIES },
          ...Object.entries(TRAIT_RARITY).map(([trait, options]) => ({ trait, options }))
        ].map(({ trait, options }) => (
          <div
            key={trait}
            className="bg-black/60 backdrop-blur-md border-2 border-mcrib-gold/50 rounded-2xl p-6"
          >
            <h3 className="text-mcrib-gold font-pixel text-lg uppercase mb-4">
              {trait === "Background" ? "BACKGROUND" : trait.toUpperCase()}
            </h3>
            {Object.entries(options).map(([name, rarity]) => (
              <motion.div
                key={name}
                whileHover={{ scale: 1.05, backgroundColor: "#ff450030" }}
                className="bg-black/50 rounded-lg p-3 text-center border border-mcrib-gold/30 mb-2 cursor-pointer"
                title={`${rarity} rarity`}
              >
                <div className="text-sm">{name}</div>
                <div className="text-xs text-mcrib-gold">
                  {rarity} rarity
                </div>
              </motion.div>
            ))}
          </div>
        ))}
      </div>

      {/* PERSONAL TRAY — ONLY CONNECTED WALLET'S McRIBS */}
<div className="max-w-7xl mx-auto mt-20 px-4">
        {activeTab === "mynfts" && (
            // CRITICAL FIX: Check the cached list of all IDs for the total count.
            allTokenIDs.length === 0 ? (
                <div className="text-center py-32">
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="inline-block"
                    >
                        <div className="bg-black/80 backdrop-blur-xl border-4 border-dashed border-mcrib-gold/60 rounded-3xl px-16 py-24 shadow-2xl">
                            <h2 className="text-7xl font-pixel text-mcrib-gold mb-6 tracking-wider opacity-90">
                                YOUR TRAY IS EMPTY
                            </h2>
                            <p className="text-3xl text-mcrib-red/90 font-pixel">
                                Mint your first McRib to fill it up
                            </p>
                            <div className="mt-10 text-mcrib-gold/50 font-pixel text-2xl">
                                The grill is hot. The sauce is dripping.
                            </div>
                        </div>
                    </motion.div>
                </div>
            ) : (
                <>
                    {/* PAGINATION CONTROLS: Added to the top of the NFT tray */}
                    {allTokenIDs.length > NFTS_PER_PAGE && (
  <div className="flex flex-col items-center gap-4 my-8">
    <div className="flex items-center gap-6">
      <button
        onClick={() => loadMyMcRibs(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-8 py-3 bg-mcrib-red/80 hover:bg-mcrib-red disabled:bg-gray-700/50 rounded-xl text-white font-bold text-xl transition"
      >
        ← Previous
      </button>

      <div className="text-center">
        <div className="text-2xl font-bold text-mcrib-gold">
          Page {currentPage} of {Math.ceil(allTokenIDs.length / NFTS_PER_PAGE)}
        </div>
        <div className="text-lg text-gray-300 mt-1">
          Showing {(currentPage - 1) * NFTS_PER_PAGE + 1}–{Math.min(currentPage * NFTS_PER_PAGE, allTokenIDs.length)} of {allTokenIDs.length}
        </div>
      </div>

      <button
        onClick={() => loadMyMcRibs(currentPage + 1)}
        disabled={currentPage === Math.ceil(allTokenIDs.length / NFTS_PER_PAGE)}
        className="px-8 py-3 bg-mcrib-red/80 hover:bg-mcrib-red disabled:bg-gray-700/50 rounded-xl text-white font-bold text-xl transition"
      >
        Next →
      </button>
    </div>

    {/* Loading text appears here */}
    {mintStep && (
      <div className="text-2xl font-pixel text-mcrib-gold animate-pulse">
        {mintStep}
      </div>
    )}
  </div>
)}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-10 justify-center">
    {myNFTs // <-- Now we just map over the already-paged array
      .map(({ id, metadata }) => (
    <motion.div
      key={id}
      whileHover={{ y: -50, scale: 1.35 }}
      transition={{ type: "spring", stiffness: 300, damping: 12 }}
      onClick={() => setSelectedNFT({ id, metadata })}
      className="relative group cursor-pointer"
    >
                                <div className="relative overflow-hidden rounded-2xl border-8 border-mcrib-gold shadow-2xl aspect-square w-72 h-72 mx-auto">
                                    {/* Lid */}
                                    <div className="absolute top-0 left-0 right-0 h-32 bg-mcrib-red/95 border-t-8 border-x-8 border-mcrib-gold rounded-t-2xl z-20 transition-transform duration-600 group-hover:-translate-y-48" />

                                    {/* McRib — FINAL BULLETPROOF RENDERER */}
                                    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
                                        {metadata.image && metadata.image.startsWith("data:image/svg+xml;base64,") ? (
                                            <div
                                                dangerouslySetInnerHTML={{
                                                    __html: atob(metadata.image.replace("data:image/svg+xml;base64,", ""))
                                                }}
                                                className="w-full h-full scale-110 group-hover:scale-160 group-hover:translate-y-20 transition-all duration-700"
                                                style={{ pointerEvents: "none" }}
                                            />
                                        ) : (
                                            <img
                                                src={metadata.image}
                                                alt={`McRib #${id}`}
                                                className="w-full h-full object-cover scale-110 group-hover:scale-160 group-hover:translate-y-20 transition-all duration-700"
                                            />
                                        )}
                                    </div>

                                    {/* Label */}
                                    <div className="absolute bottom-6 left-0 right-0 text-center z-30 space-y-3">
                                        <p className="font-pixel text-mcrib-gold text-3xl drop-shadow-2xl bg-black/80 px-8 py-3 rounded-full inline-block">
                                            McRIB #{id}
                                        </p>

                                        {/* EAT BUTTON — ONLY SHOWS IF NOT ALREADY EATEN */}
                                        {metadata.attributes?.some(attr => attr.trait_type === "Status" && attr.value === "Eaten") ? (
                                            <div className="bg-gray-800/90 px-6 py-2 rounded-full inline-block">
                                                <span className="text-gray-500 font-pixel text-lg">EATEN</span>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedNFT({ id, metadata });
                                                }}
                                                className="bg-gradient-to-red hover:bg-red-700 text-white font-pixel text-xl px-8 py-3 rounded-full shadow-lg transform hover:scale-110 transition"
                                            >
                                                EAT THIS McRIB
                                            </button>
                                        )}
                                    </div>

                                    {/* Fire glow */}
                                    <div className="absolute inset-0 bg-mcrib-fire/40 blur-3xl scale-150 group-hover:scale-200 transition-all duration-500 -z-10" />
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* PAGINATION CONTROLS: Added to the bottom of the NFT tray */}
                    {allTokenIDs.length > NFTS_PER_PAGE && (
                        <div className="flex justify-center space-x-4 mt-8">
                            {/* Helper functions for pagination UI */}
                            {(function() {
                                const totalPages = Math.ceil(allTokenIDs.length / NFTS_PER_PAGE);
                                const handlePageChange = (newPage) => {
                        if (newPage >= 1 && newPage <= totalPages) {
                            setCurrentPage(newPage);
                            loadMyMcRibs(newPage); // <-- ADDED
                        }
                    };
                                
                                return (
                                    <>
                                        <button 
                                            onClick={() => handlePageChange(currentPage - 1)} 
                                            disabled={currentPage === 1}
                                            className="px-6 py-3 bg-mcrib-red/80 hover:bg-mcrib-red disabled:bg-gray-700/50 rounded-lg text-white font-bold transition duration-150"
                                        >
                                            ← Previous Page
                                        </button>
                                        <span className="px-6 py-3 text-xl font-bold text-mcrib-gold border-2 border-mcrib-gold rounded-lg backdrop-blur-sm">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button 
                                            onClick={() => handlePageChange(currentPage + 1)} 
                                            disabled={currentPage === totalPages || totalPages === 0}
                                            className="px-6 py-3 bg-mcrib-red/80 hover:bg-mcrib-red disabled:bg-gray-700/50 rounded-lg text-white font-bold transition duration-150"
                                        >
                                            Next Page →
                                        </button>
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </>
            )
        )}
      </div>

      {/* NFT MODAL — TALL BADDIE EDITION */}
      {selectedNFT && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-6 md:p-12 overflow-y-auto"
          onClick={() => setSelectedNFT(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="relative bg-black/90 backdrop-blur-xl rounded-3xl border-4 border-mcrib-gold shadow-3xl max-w-6xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button — Top Right */}
            <button
              onClick={() => setSelectedNFT(null)}
              className="absolute top-4 right-4 bg-mcrib-red/90 hover:bg-mcrib-red text-white px-6 py-3 rounded-full font-bold text-lg z-10 shadow-lg hover:scale-110 transition"
            >
              CLOSE ×
            </button>

            <div className="flex flex-col lg:flex-row gap-8 p-8 lg:p-12">
              {/* LEFT: FULL HEIGHT McRIB IMAGE + LONG EAT BUTTON */}
              <div className="flex-1 flex flex-col items-center justify-center min-h-96 lg:min-h-full space-y-6">
                <div className="relative w-full max-w-md">
                  {selectedNFT.metadata.image && selectedNFT.metadata.image.startsWith("data:image/svg+xml;base64,") ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: atob(selectedNFT.metadata.image.replace("data:image/svg+xml;base64,", ""))
                      }}
                      className="w-full h-auto rounded-3xl shadow-2xl overflow-hidden"
                      style={{ maxHeight: '80vh' }}
                    />
                  ) : (
                    <img
                      src={selectedNFT.metadata.image}
                      alt={`McRib #${selectedNFT.id}`}
                      className="w-full h-auto rounded-3xl shadow-2xl"
                      style={{ maxHeight: '80vh', objectFit: 'contain' }}
                    />
                  )}
                </div>

                {/* ONLY ONE EAT BUTTON — UNDER THE IMAGE — THIS IS THE CORRECT ONE */}
                {selectedNFT.metadata.attributes?.some(attr => attr.trait_type === "Status" && attr.value === "Eaten") ? (
                  <div className="w-full max-w-md text-center">
                    <div className="bg-gray-800/90 px-12 py-4 rounded-3xl inline-block">
                      <span className="text-3xl font-pixel text-gray-500">ALREADY EATEN</span>
                    </div>
                  </div>
                ) : (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleEatMcRib(selectedNFT.id)}
                    disabled={isEating}
                    className="w-full max-w-md bg-gradient-to-r from-red-600 to-red-800 hover:from-red-700 hover:to-red-900 text-white font-pixel text-4xl py-6 rounded-3xl shadow-2xl disabled:opacity-50"
                  >
                    {isEating ? "EATING..." : "EAT THIS McRIB"}
                  </motion.button>
                )}
              </div>

              {/* RIGHT: TITLE + TRAITS (2 COLUMNS) — NO MORE EAT BUTTON HERE */}
              <div className="flex-1 flex flex-col justify-center space-y-6">
                <div className="text-center lg:text-left">
                  <h2 className="text-5xl md:text-6xl font-pixel text-mcrib-gold drop-shadow-2xl">
                    McRib #{selectedNFT.id}
                  </h2>
                  <p className="text-xl text-mcrib-red/90 mt-2 font-bold">Fresh off the grill</p>
                </div>

                {/* Traits Grid — 2 Columns — CLEAN, NO BUTTONS */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                  {selectedNFT.metadata.attributes?.map((attr) => (
                    <div
                      key={attr.trait_type}
                      className="bg-gradient-to-br from-mcrib-gold/20 to-mcrib-fire/10 border-2 border-mcrib-gold/50 rounded-xl p-5 text-center backdrop-blur-sm"
                    >
                      <div className="text-sm text-gray-400 font-pixel uppercase tracking-wider">
                        {attr.trait_type}
                      </div>
                      <div className="text-xl font-bold text-mcrib-gold mt-1">
                        {attr.value}
                      </div>
                    </div>
                  ))}
                </div>

                {/* EVERYTHING BELOW THIS LINE HAS BEEN REMOVED — NO DUPLICATE BUTTONS */}
                {/* We deleted the entire duplicate EAT button + EATEN state that was under the traits */}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      <div className="text-center mt-20 pb-20">
        <p className="text-3xl font-pixel text-mcrib-gold">
          {mintedCount} / 10,000 MINTED
        </p>
      </div>
    </div>
  );
}